import { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper to convert hex to HSL for Tailwind CSS variables
const hexToHSL = (hex) => {
  if (!hex) return null;
  hex = hex.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;
  
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h, s, l = (max + min) / 2;

  if (max === min) {
    h = s = 0;
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
      default: h = 0;
    }
  }

  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
};

// Apply branding to document root
const applyBrandingToDOM = (branding) => {
  if (!branding) return;
  
  const root = document.documentElement;
  const colors = branding.colors || {};
  
  // Apply primary color
  if (colors.primary) {
    const hsl = hexToHSL(colors.primary);
    if (hsl) {
      root.style.setProperty('--primary', hsl);
      root.style.setProperty('--primary-700', colors.primary);
    }
  }
  
  // Apply secondary color
  if (colors.secondary) {
    const hsl = hexToHSL(colors.secondary);
    if (hsl) {
      root.style.setProperty('--secondary', hsl);
      root.style.setProperty('--secondary-600', colors.secondary);
    }
  }
  
  // Apply accent color
  if (colors.accent) {
    root.style.setProperty('--accent-color', colors.accent);
  }
  
  // Apply background color
  if (colors.background) {
    root.style.setProperty('--background-color', colors.background);
    // If dark background, switch to dark mode
    const isDark = isColorDark(colors.background);
    if (isDark) {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }
  
  // Apply text color
  if (colors.text) {
    root.style.setProperty('--text-color', colors.text);
  }
  
  // Apply logo to favicon if provided
  if (branding.favicon_url) {
    const link = document.querySelector("link[rel*='icon']") || document.createElement('link');
    link.type = 'image/x-icon';
    link.rel = 'shortcut icon';
    link.href = branding.favicon_url;
    document.getElementsByTagName('head')[0].appendChild(link);
  }
  
  // Apply custom CSS if provided
  if (branding.custom_css) {
    let styleEl = document.getElementById('custom-branding-css');
    if (!styleEl) {
      styleEl = document.createElement('style');
      styleEl.id = 'custom-branding-css';
      document.head.appendChild(styleEl);
    }
    styleEl.textContent = branding.custom_css;
  }
  
  console.log('Branding applied:', { colors, logo: branding.logo_url });
};

// Check if a color is dark
const isColorDark = (hex) => {
  if (!hex) return false;
  hex = hex.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance < 0.5;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [companyBranding, setCompanyBranding] = useState(null);

  // Apply branding whenever it changes
  useEffect(() => {
    if (companyBranding) {
      console.log('Applying branding from AuthContext:', companyBranding);
      applyBrandingToDOM(companyBranding);
    }
  }, [companyBranding]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      
      // Fetch company branding for company users using new versioned API
      if (response.data.role && ["company_admin", "company_user", "admin"].includes(response.data.role)) {
        try {
          const brandingRes = await axios.get(`${API}/branding`);
          console.log('Branding API response:', brandingRes.data);
          // Map the new versioned response format
          if (brandingRes.data.settings) {
            const brandingData = {
              ...brandingRes.data.settings,
              version: brandingRes.data.version,
              etag: brandingRes.data.etag,
            };
            console.log('Setting companyBranding:', brandingData);
            setCompanyBranding(brandingData);
          } else {
            setCompanyBranding(brandingRes.data);
          }
        } catch (e) {
          console.error('Branding fetch error:', e);
          // Branding not available, use defaults
        }
      }
    } catch (error) {
      localStorage.removeItem("token");
      delete axios.defaults.headers.common["Authorization"];
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token, user: userData } = response.data;
    localStorage.setItem("token", token);
    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    setUser(userData);
    
    // Fetch branding after login for company users using new versioned API
    if (userData.role && ["company_admin", "company_user", "admin"].includes(userData.role)) {
      try {
        const brandingRes = await axios.get(`${API}/branding`);
        if (brandingRes.data.settings) {
          setCompanyBranding({
            ...brandingRes.data.settings,
            version: brandingRes.data.version,
            etag: brandingRes.data.etag,
          });
        } else {
          setCompanyBranding(brandingRes.data);
        }
      } catch (e) {
        // Ignore
      }
    }
    
    return userData;
  };

  const register = async (name, email, password, entityName, referralCode = null, entitySlug = null) => {
    const payload = {
      name,
      email,
      password,
      entity_name: entityName,
    };
    
    // Add optional fields if provided
    if (referralCode) {
      payload.referral_code = referralCode;
    }
    if (entitySlug) {
      payload.entity_slug = entitySlug;
    }
    
    const response = await axios.post(`${API}/auth/register`, payload);
    const { token, user: userData } = response.data;
    localStorage.setItem("token", token);
    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem("token");
    delete axios.defaults.headers.common["Authorization"];
    setUser(null);
    setCompanyBranding(null);
    
    // Also call SSO logout endpoint
    axios.post(`${API}/sso/logout`).catch(() => {});
  };

  const loginWithSSO = async (token, userData) => {
    localStorage.setItem("token", token);
    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    setUser(userData);
    
    // Fetch branding after SSO login for company users using new versioned API
    if (userData.role && ["company_admin", "company_user", "admin"].includes(userData.role)) {
      try {
        const brandingRes = await axios.get(`${API}/branding`);
        if (brandingRes.data.settings) {
          setCompanyBranding({
            ...brandingRes.data.settings,
            version: brandingRes.data.version,
            etag: brandingRes.data.etag,
          });
        } else {
          setCompanyBranding(brandingRes.data);
        }
      } catch (e) {
        // Ignore
      }
    }
    
    return userData;
  };

  const refreshUser = async () => {
    await fetchUser();
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      login, 
      register, 
      logout, 
      loginWithSSO,
      refreshUser,
      companyBranding 
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
